import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "drop-overlay",
  "data-testid": "file-selector-text-area__drop-overlay"
}
const _hoisted_2 = ["id"]
const _hoisted_3 = {
  key: 1,
  class: "file-selector-row"
}
const _hoisted_4 = { class: "drop-hint" }

import {
  computed, ref, useAttrs, useId, type StyleValue
} from 'vue';
import { useStore } from 'vuex';
import { LabeledInput } from '@components/Form/LabeledInput';
import FileSelector, { readFileContents } from '@shell/components/form/FileSelector.vue';
import { useI18n } from '@shell/composables/useI18n';
import { _VIEW } from '@shell/config/query-params';
import type { Validator } from '@shell/utils/validators/formRules/index';

type TransformFile = (contents: string) => string;


export default /*@__PURE__*/_defineComponent({
  ...{ inheritAttrs: false },
  __name: 'FileSelectorTextArea',
  props: {
    value: { default: '' },
    label: { default: undefined },
    labelKey: { default: undefined },
    subLabel: { default: undefined },
    placeholder: { default: undefined },
    placeholderKey: { default: undefined },
    mode: { default: undefined },
    name: { default: undefined },
    type: { default: 'multiline' },
    required: { type: Boolean },
    disabled: { type: Boolean },
    rules: { default: () => [] },
    minHeight: { default: 56 },
    maxHeight: { default: 200 },
    accept: { default: '*' },
    byteLimit: { default: 0 },
    fileSelectorTestid: { default: undefined },
    transformFile: { type: Function, default: undefined }
  },
  emits: ["update:value"],
  setup(__props: any, { emit: __emit }) {

/**
 * A multiline text field whose contents can also be populated from a file,
 * either with the "Read from File" button or by dropping a file onto the text
 * area. Used for pasted blobs such as certificates and private keys.
 */


const props = __props;

const emit = __emit;

const store = useStore();
const { t } = useI18n(store);
const attrs = useAttrs();

// class/style belong on the wrapper, every other attribute (data-testid, aria-*)
// belongs on the text area itself
const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs;

  return rest;
});
// Binding an undefined data-testid would strip the one FileSelector sets for itself
const fileSelectorAttrs = computed(() => (props.fileSelectorTestid ? { 'data-testid': props.fileSelectorTestid } : {}));
const rootClass = computed(() => attrs.class as string | string[] | Record<string, boolean> | undefined);
const rootStyle = computed(() => attrs.style as StyleValue);

const subLabelId = useId();
const describedBy = computed(() => [attrs['aria-describedby'], props.subLabel && subLabelId].filter(Boolean).join(' ') || undefined);

// Nested elements fire their own dragenter/dragleave, so count entries and
// exits rather than toggling a boolean, otherwise the overlay flickers.
const dragDepth = ref(0);

// Tells a dragged file apart from text dragged around the page
const isFileDrag = (event: DragEvent) => Array.from(event.dataTransfer?.types || []).includes('Files');

// The button always reads "Read from File", so two of these on one form — a private key and a
// certificate, say — give a screen reader two buttons with identical names and nothing to tell them
// apart. The visible text stays as designed; the accessible name carries the field it fills.
const fieldLabel = computed(() => props.label || (props.labelKey ? t(props.labelKey) : ''));
const fileSelectorLabel = computed(() => (fieldLabel.value ? t('generic.readFromFileArea', { area: fieldLabel.value }) : t('generic.readFromFile')));

const isView = computed(() => props.mode === _VIEW);
const isDropTarget = computed(() => !isView.value && !props.disabled);
const isDragging = computed(() => dragDepth.value > 0);

const onSelected = (contents: string) => emit('update:value', props.transformFile ? props.transformFile(contents) : contents);

const onError = (error: unknown) => {
  // `err`, not `error`: growl/fromError reads `err`, so the other spelling threw the detail away and
  // left the growl with a title and no body.
  store.dispatch('growl/fromError', { title: t('generic.errorReadingFile'), err: error }, { root: true });
};

const onDragEnter = (event: DragEvent) => {
  if (isDropTarget.value && isFileDrag(event)) {
    dragDepth.value++;
  }
};

const onDragOver = (event: DragEvent) => {
  if (isDropTarget.value && isFileDrag(event)) {
    // Without this the browser opens the dropped file instead of letting us read it
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }
};

const onDragLeave = () => {
  dragDepth.value = Math.max(0, dragDepth.value - 1);
};

const onDrop = async(event: DragEvent) => {
  if (!isDropTarget.value || !isFileDrag(event)) {
    return;
  }

  event.preventDefault();
  dragDepth.value = 0;

  const file = event.dataTransfer?.files?.[0];

  if (!file) {
    return;
  }

  if (props.byteLimit && file.size > props.byteLimit) {
    onError(t('generic.byteLimitExceeded', { name: file.name, byteLimit: props.byteLimit }));

    return;
  }

  try {
    onSelected(await readFileContents(file));
  } catch (error) {
    onError(error);
  }
};

return (_ctx: any,_cache: any) => {
  const _directive_clean_html = _resolveDirective("clean-html")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["file-selector-text-area", rootClass.value]),
    style: _normalizeStyle(rootStyle.value)
  }, [
    _createElementVNode("div", {
      class: "drop-zone",
      onDragenter: onDragEnter,
      onDragover: onDragOver,
      onDragleave: onDragLeave,
      onDrop: onDrop
    }, [
      _createVNode(_unref(LabeledInput), _mergeProps(inputAttrs.value, {
        type: __props.type,
        value: __props.value,
        label: __props.label,
        "label-key": __props.labelKey,
        placeholder: __props.placeholder,
        "placeholder-key": __props.placeholderKey,
        mode: __props.mode,
        name: __props.name,
        required: __props.required,
        disabled: __props.disabled,
        rules: __props.rules,
        "min-height": __props.minHeight,
        "max-height": __props.maxHeight,
        "resize-on-value-change-and-resize-window": true,
        "aria-describedby": describedBy.value,
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('update:value', $event)))
      }), null, 16, ["type", "value", "label", "label-key", "placeholder", "placeholder-key", "mode", "name", "required", "disabled", "rules", "min-height", "max-height", "aria-describedby"]),
      _cache[1] || (_cache[1] = _createTextVNode()),
      (isDragging.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("span", null, _toDisplayString(_unref(t)('fileSelectorTextArea.dropToReplace')), 1)
          ]))
        : _createCommentVNode("", true)
    ], 32),
    _cache[3] || (_cache[3] = _createTextVNode()),
    (__props.subLabel)
      ? _withDirectives((_openBlock(), _createElementBlock("div", {
          key: 0,
          id: _unref(subLabelId),
          class: "sub-label",
          "data-testid": "file-selector-text-area__sub-label"
        }, null, 8, _hoisted_2)), [
          [_directive_clean_html, __props.subLabel]
        ])
      : _createCommentVNode("", true),
    _cache[4] || (_cache[4] = _createTextVNode()),
    (!isView.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(FileSelector, _mergeProps(fileSelectorAttrs.value, {
            variant: "tertiary",
            mode: __props.mode,
            disabled: __props.disabled,
            accept: __props.accept,
            "byte-limit": __props.byteLimit,
            "show-growl-error": false,
            "aria-label": fileSelectorLabel.value,
            label: _unref(t)('generic.readFromFile'),
            onSelected: onSelected,
            onError: onError
          }), null, 16, ["mode", "disabled", "accept", "byte-limit", "aria-label", "label"]),
          _cache[2] || (_cache[2] = _createTextVNode()),
          _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('fileSelectorTextArea.dropHint')), 1)
        ]))
      : _createCommentVNode("", true)
  ], 6))
}
}

})